import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { AdminService } from 'src/app/services/admin.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-user-completed-tasks',
  templateUrl: './user-completed-tasks.component.html',
  styleUrls: ['./user-completed-tasks.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, SkeletonModule],
})
export class UserCompletedTasksComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() projectId: string | null = null;
  @Input() taskGroupId: string | null = null; // Add task group filter
  /** Shows Hide/Show to open and close the list (Tasks List page only, 30 Oct 2026). */
  @Input() collapsible = false;
  @Output() onToggle = new EventEmitter<any>();

  /** Closed state; remembered in this browser so it stays the same on the next visit. */
  isCollapsed = false;
  private static readonly COLLAPSED_KEY = 'pmtCompletedTasksCollapsed';

  tasks: any[] = [];
  loading: boolean = false;
  page: number = 1;
  limit: number = 10;
  hasMore: boolean = true;
  totalCount: number = 0;
  searchTerm: string = '';
  private searchSubject = new Subject<string>();

  constructor(
    private adminService: AdminService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.collapsible) {
      try {
        this.isCollapsed =
          localStorage.getItem(UserCompletedTasksComponent.COLLAPSED_KEY) === '1';
      } catch {
        this.isCollapsed = false;
      }
    }

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.resetAndLoad();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['taskGroupId']) && this.projectId) {
      this.resetAndLoad();
    }
  }

  resetAndLoad() {
    this.tasks = [];
    this.page = 1;
    this.hasMore = true;
    this.loadTasks();
  }

  loadTasks() {
    if (!this.projectId || this.loading || !this.hasMore) return;

    this.loading = true;
    this.adminService
      .getCompletedTasks(
        this.projectId,
        this.page,
        this.limit,
        this.taskGroupId || undefined,
        this.searchTerm || undefined,
      )
      .subscribe({
        next: (res: any) => {
          if (res.Code === 200 && res.data) {
            const newTasks = res.data;
            this.tasks = [...this.tasks, ...newTasks];
            this.totalCount = res.totalCount;

            if (newTasks.length < this.limit) {
              this.hasMore = false;
            } else {
              this.page++;
            }
          } else {
            this.hasMore = false;
          }
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching completed tasks:', err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  toggleCollapsed(): void {
    if (!this.collapsible) return;
    this.isCollapsed = !this.isCollapsed;
    try {
      localStorage.setItem(
        UserCompletedTasksComponent.COLLAPSED_KEY,
        this.isCollapsed ? '1' : '0',
      );
    } catch {
      // Storage blocked (e.g. private mode): the choice just isn't remembered.
    }
  }

  prependTask(task: any) {
    // Avoid duplicates
    if (this.tasks.some((t) => t._id === task._id)) return;

    this.tasks = [task, ...this.tasks];
    this.totalCount++;
    this.cdr.markForCheck();
  }

  removeTask(taskId: string) {
    const initialLength = this.tasks.length;
    this.tasks = this.tasks.filter((t) => t._id !== taskId);
    if (this.tasks.length < initialLength) {
      this.totalCount = Math.max(0, this.totalCount - 1);
      this.cdr.markForCheck();
      return;
    }

    // Check subtasks in map
    for (const parentId in this.subtaskMap) {
      if (this.subtaskMap[parentId]) {
        const subIndex = this.subtaskMap[parentId].findIndex(
          (s) => s._id === taskId,
        );
        if (subIndex !== -1) {
          this.subtaskMap[parentId].splice(subIndex, 1);
          // Don't decrement totalCount for subtasks as it tracks main tasks?
          // The header says "Completed Tasks (N)".
          // `getCompletedTasks` endpoint returns `totalCount` of MAIN tasks.
          // So we shouldn't decrement totalCount if a subtask is removed.
          this.cdr.markForCheck();
          return;
        }
      }
    }
    this.cdr.markForCheck();
  }

  onScroll(event: any) {
    const element = event.target;
    // Check if scrolled near bottom
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      this.loadTasks();
    }
  }

  toggleCompletion(task: any, event: Event): void {
    if (this.onToggle) {
      this.onToggle.emit({ task, event });
      // Optimistic update: remove query from list or change status,
      // but since parent might refresh everything, maybe just emit is enough.
      // However, user might expect immediate UI update. For now, let's rely on parent refresh or manual removal.
      // Actually, we should probably remove it from the list locally to reflect "not completed anymore"
      // if the toggle is about un-completing.
      // But wait, the parent `ProjectDetailsComponent` handles the toggle logic and might refresh the whole page.
      // If so, our component might be re-initialized or projectId might change (unlikely).
      // Ideally we should just emit and let parent handle it.
    }
  }

  getStatusClass(statusName: string | undefined): string {
    if (!statusName) return 'status-na';
    return 'status-' + statusName.toLowerCase().replace(/\s+/g, '-');
  }

  getStatusLabel(status: any): string {
    return status?.name || 'N/A';
  }

  getStatusColor(status: any): string {
    return status?.colorCode || '#ccc';
  }

  getPriorityClass(priority: string): string {
    if (!priority) return '';
    return 'priority-' + priority.toLowerCase();
  }

  getProgressColorClass(percentage: number): string {
    if (percentage >= 76) {
      return 'progress-76-100';
    } else if (percentage >= 51) {
      return 'progress-51-75';
    } else if (percentage >= 26) {
      return 'progress-26-50';
    } else {
      return 'progress-0-25';
    }
  }

  getProfileImage(user: any): string {
    return (
      user?.profileImg || user?.avatar || 'assets/images/defaults/user.jpg'
    );
  }

  generateAvatar(name: string): string {
    // rudimentary avatar generation if image fails
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
  }

  onImgError(event: any, fallbackUrl: string) {
    event.target.src = fallbackUrl;
  }

  // Subtask Handling
  expandedTaskId: string | null = null;
  subtaskMap: { [key: string]: any[] } = {};

  toggleSubtasks(task: any): void {
    if (this.expandedTaskId === task._id) {
      this.expandedTaskId = null;
    } else {
      this.expandedTaskId = task._id;
      if (!this.subtaskMap[task._id]) {
        this.loadSubtasksForTaskTable(task._id);
      }
    }
  }

  loadSubtasksForTaskTable(taskId: string): void {
    this.adminService.getSubtasksByTaskId(taskId).subscribe({
      next: (res) => {
        // We only want to show COMPLETED subtasks in this view?
        // Or all subtasks?
        // User said "SHOW LIKE PARENT CHILD".
        // If the parent is completed, ALL subtasks SHOULD be completed.
        // But if there are extra subtasks added later?
        // Let's show all, but visually distinguish.
        // Actually, for "Completed Tasks" view, usually we assume they are completed.
        // But let's trust the backend response.
        this.subtaskMap[taskId] = res?.data || [];
      },
      error: (err) => {
        console.error('Error loading subtasks:', err);
        this.subtaskMap[taskId] = [];
      },
    });
  }

  // Helper to format date if needed, though pipe is used in template
}
